import { conns } from '@cfg/axiosConfig.tsx';
import type {
  LegacyWebBlockEditContext,
  LegacyWebBlockEditUpdate,
  LegacyWebBlockReorder,
  LegacyWebBlock,
  LegacyWebCatalog,
  LegacyWebChildBlockCreate,
  LegacyWebConfirmResponse,
  LegacyWebDraftResponse,
  LegacyWebFeaturedProductsUpdate,
  LegacyWebLanding,
  LegacyWebNavigationResponse,
  LegacyWebPage,
  LegacyWebSection,
} from './types';

function readParams(previewToken?: string | null, cacheBust = Date.now()) {
  return {
    ...(previewToken ? { preview_token: previewToken } : {}),
    // Legacy CMS responses are mutable outside of React Query. Vary the URL so
    // an old browser/proxy response cannot survive a block edit or deletion.
    _cms_cache_bust: cacheBust,
  };
}

export async function getLegacyWebPage(
  slug: string,
  previewToken?: string | null,
  cacheBust = Date.now(),
) {
  const params = readParams(previewToken, cacheBust);
  const { data } = await conns<LegacyWebPage>('cms:legacy_web', ['pages', slug]).get(params);
  return data;
}

export async function getLegacyWebLanding(
  previewToken?: string | null,
  cacheBust = Date.now(),
) {
  const params = readParams(previewToken, cacheBust);
  const { data } = await conns<LegacyWebLanding>('cms:legacy_web', ['landing']).get(params);
  return data;
}

export async function getLegacyWebNavigation() {
  const { data } = await conns<LegacyWebNavigationResponse>(
    'cms:legacy_web',
    ['navigation'],
  ).get({ _cms_cache_bust: Date.now() });
  return data;
}

const STYLE13_SECTION_SLUGS = ['home', 'quienes-somos', 'como-comprar', 'contacto'] as const;

export async function hydrateStyle13Landing(
  landing: LegacyWebLanding,
  previewToken?: string | null,
  cacheBust = Date.now(),
) {
  if (landing.style_id !== 13) return landing;
  const pages = await Promise.all(
    STYLE13_SECTION_SLUGS.map(async (slug) => {
      try {
        return await getLegacyWebPage(slug, previewToken, cacheBust);
      } catch {
        return null;
      }
    }),
  );
  const sections = pages.filter((page): page is LegacyWebPage => Boolean(page));
  return {
    ...landing,
    sections: sections.length ? sections : landing.sections,
    navigation: landing.navigation?.filter((item) => item.id !== 2 && item.slug?.trim().toLowerCase() !== 'linea-premium'),
  } satisfies LegacyWebLanding;
}

export async function getLegacyWebCatalog() {
  const { data } = await conns<LegacyWebCatalog>('cms:legacy_web', ['catalog']).get();
  return data;
}

export async function getLegacyWebSections() {
  const { data } = await conns<LegacyWebSection[]>('cms:legacy_web', ['sections']).get();
  return data;
}

export async function getLegacyWebBlockEditContext(blockId: number) {
  const { data } = await conns<LegacyWebBlockEditContext>('cms:legacy_web', ['blocks', blockId, 'edit']).get();
  return data;
}

export async function updateLegacyWebBlock(blockId: number, payload: LegacyWebBlockEditUpdate) {
  const { data } = await conns<LegacyWebPage>('cms:legacy_web', ['blocks', blockId]).patch(payload);
  return data;
}

export async function previewLegacyWebBlock(blockId: number, payload: LegacyWebBlockEditUpdate) {
  const { data } = await conns<LegacyWebBlock>('cms:legacy_web', ['blocks', blockId, 'preview']).post(payload);
  return data;
}

export async function createLegacyWebChildBlock(parentId: number, payload: LegacyWebChildBlockCreate) {
  const { data } = await conns<LegacyWebPage>('cms:legacy_web', ['blocks', parentId, 'children']).post(payload);
  return data;
}

export async function updateLegacyWebFeaturedProducts(
  blockId: number,
  payload: LegacyWebFeaturedProductsUpdate,
) {
  const { data } = await conns<LegacyWebPage>(
    'cms:legacy_web',
    ['blocks', blockId, 'featured-products'],
  ).patch(payload);
  return data;
}

export async function reorderLegacyWebBlock(blockId: number, payload: LegacyWebBlockReorder) {
  const { data } = await conns<LegacyWebPage>('cms:legacy_web', ['blocks', blockId, 'reorder']).patch(payload);
  return data;
}

export async function duplicateLegacyWebBlock(blockId: number) {
  const { data } = await conns<LegacyWebBlock>('cms:legacy_web', ['blocks', blockId, 'duplicate']).post();
  return data;
}

export async function deleteLegacyWebBlock(blockId: number) {
  await conns('cms:legacy_web', ['blocks', blockId]).delete();
  return { ok: true };
}

export async function createLegacyWebDraft(slug: string, page: LegacyWebPage) {
  const { data } = await conns<LegacyWebDraftResponse>('cms:legacy_web', ['drafts']).post({ slug, page });
  return data;
}

export async function confirmLegacyWebDraft(slug: string, token: string) {
  const { data } = await conns<LegacyWebConfirmResponse>('cms:legacy_web', ['drafts', slug, token, 'confirm']).post();
  return data;
}

export async function discardLegacyWebDraft(slug: string, token: string) {
  await conns('cms:legacy_web', ['drafts', slug, token]).delete();
  return { ok: true };
}
